import { HttpContextToken } from "@angular/common/http";
import { TooltipPosition } from "@angular/material/tooltip";
import moment from "moment";
import { generateGUID } from "../functions/guid-function";

export declare type ButtonColor = "basic" | "primary" | "warn" | "accent";
declare type Appearance = 'fill' | 'outline';
declare type Snackbartype = 'sucess' | 'error' | 'warning' | 'info';
declare type labelPosition = 'before' | 'after';
declare type orientation = 'horizontal' | 'vertical';

export const SYSTEM_CONST = {
  ACCENT: 'accent' as ButtonColor,
  ADD: 'add',
  AFTER: 'after' as labelPosition,
  BASIC: 'basic' as ButtonColor,
  BEFORE: 'before' as labelPosition,
  CANCEL: 'cancel',
  CHECKBOX: 'checkbox',
  COMMONGRID: 'commonGrid',
  DASHBOARD: 'dashboard',
  DATE: 'date',
  DOWNLOAD: 'download',
  DELETE: 'delete',
  EDIT: 'edit',
  CREATE: 'create',
  ERROR: 'error' as Snackbartype,
  FILL: 'fill' as Appearance,
  ID: 'id',
  INFO: 'info' as Snackbartype,
  NAME: 'name',
  NEW: 'new',
  NO: 'no',
  OUTLINE: 'outline' as Appearance,
  PERCENTAGE: 'percentage',
  PRIMARY: 'primary' as ButtonColor,
  SAVE: 'save',
  SEARCH: 'search',
  CONFIRM: 'COMMON.CONFIRM',
  SUCESS_TITLE: 'Sucess',
  ERROR_TITLE: 'Error',
  STRING: 'string',
  SUCESS: 'sucess' as Snackbartype,
  TYPE: 'type',
  VIEW: 'view',
  VISIBILITY: 'visibility',
  WARN: 'warn' as ButtonColor,
  WARNING: 'warning' as Snackbartype,
  YES: 'yes',
  VERTICAL: "vertical" as orientation,
  HORIZONTAL: "horizontal" as orientation,
  BUTTON_TITLE: {
    SAVE: 'Save',
    DELETE: 'Delete',
    RESET: 'Reset',
    CANCEL: 'Cancel',
    CREATE: 'Create',
    VIEW: 'View',
    DOWNLOAD: 'Download',
    EDIT: 'Edit',
    SEARCH: 'Search'
  },
  TOOLTIPPOSITION: {
    ABOVE: 'above' as TooltipPosition,
    BELOW: 'before' as TooltipPosition,
    AFTER: 'after' as TooltipPosition,
    BEFORE: 'before' as TooltipPosition,
    LEFT: 'left' as TooltipPosition,
    RIGHT: 'right' as TooltipPosition
  },
  KEYCLOAK_ATTRIBUTES: {
    IS_SUPER_ADMIN: 'isSuperAdmin',
    IS_ADMIN: 'isAdmin',
    USER_TYPE: 'userType',
    PAYER: 'payer',
    PROVIDER: 'provider',
    PAYERS: 'payers',
    PROVIDERS: 'providers',
    PATIENT: 'patient'
  }
};

export interface CommonLov {
  lovId: string;
  nameEnglish: string;
  nameArabic: string;
  entityTypeId: string;
  entityTypeDescription: string;
  descriptionEnglish?: string;
  descriptionArabic?: string;
}

export const DATE_FORMATS = {
  SHORT_DATE: 'dd MMM yyyy',
  LONG_DATE_WITH_TIME: 'dd MMM yyyy hh:mm:ss a',
  API_LONG_DATE_WITH_TIME: 'yyyy-MM-DDTHH:mm:ss',
  DATE_WITH_DAY_NAME_AND_MONTH_IN_STRING: 'EEEE, d MMMM, y',
  SHORT_TIME: 'h:mm a',
  MONTH_WITH_YEAR: 'MMM, yyyy'
};

export const SESSION_TIMEOUT = {
  DIALOG_BEFORE_TIME: 2,
  CONTINUE: 'continue',
  LOGOUT: 'logout',
  STORAGE_NAME: 'sessionTimeout'
};

type CallbackFunction = (event?) => void;

export function getToolbarButtonConfig(
  callback: CallbackFunction,
  buttonTextKey: string,
  icon: string,
  variant?: 'raised' | 'flat' | 'stroked' | 'fab' | 'icon',
  color?: 'basic' | 'primary' | 'warn' | 'accent',
  id?: string,
  tooltipTextKey?: string,
  cssClasses?: string[],
  badge?: string,
  isFilterButton?: boolean,
  visibleCallback?: () => boolean,
) {
  return {
    callback,
    buttonText: buttonTextKey,
    iconUrl: './assets/images/' + icon,
    variant: variant || 'raised',
    color: color || 'primary',
    id: (id || 'btn') + generateGUID(),
    tooltipText: tooltipTextKey || buttonTextKey,
    cssClasses: cssClasses || ['btn', 'btn-secondary', 'responsive-btn-text'],
    badge: badge || '',
    isFilterButton: isFilterButton || false,
    visibleCallback
  };
}

export const FILTER_BUTTON_CONFIG = {
  buttonTextKey: 'FORM_FIELD.FILTER',
  icon: 'ic-filter.svg',
  variant: 'raised',
  color: 'basic',
  id: 'btn',
  tooltipText: 'FORM_FIELD.FILTER',
  cssClasses: ['btn', 'btn-secondary', 'count-visible', 'responsive-btn-text'],
  badge: '',
  isFilterButton: true,
};

export const EMPTY_GUID: string = '00000000-0000-0000-0000-000000000000';
export const EMPTY_GUID_REGEX: RegExp = new RegExp(`^${EMPTY_GUID}$`);
export const SKIP_ERROR_TOASTY = new HttpContextToken<boolean>(() => false);
export const NUMBER_REGEX: RegExp = /^\d{1,7}(\.\d{1,2})?$/;
export const GUID_FORMAT_REGEX: RegExp = /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;
export const NULL_VIEW_SYMBOL: string = "-";
export const CONTACT_NUMBER_MASK: string = '00 000 0000||00 000 000 0000';
export const ZIPCODE_REGEX: RegExp = /^[1-8][0-9]{4}$/;
export const BANK_ACCOUNT_REGEX: RegExp = /^\d{10,18}$/;
export const IBAN_REGEX: RegExp = /^[Ss][Aa]\d{22}$/;
export const PHYSICIAN_REG_NUM_REGEX: RegExp = /^[A-Za-z0-9]{5,20}$/;
export const BORDER_NO_REGEX: RegExp = /^\d{10}$/;
export const PASSPORT_NO_REGX: RegExp = /^[A-Za-z0-9]{6,9}$/;
export const CLINIC_NAME_REGEX: RegExp = /^[A-Za-z0-9 .,-]{2,50}$/;
export const EMAIL_REGEX: RegExp = /^(?!.*\.\.)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
export const COUNTRY_CODE_REGEX: RegExp = /^\+\d{1,3}$/;
export const INT_NUM_REGEX: RegExp = /^\d+$/;
export const PASSWORD_REGEX: RegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^_(){}<>\-+="',.:;\[\]|/\\`~])[A-Za-z\d@$!%*?&#^_(){}<>\-+="',.:;\[\]|/\\`~]{8,}$/;
export const WEBSITE_REGEX: RegExp = /^((ftp|http|https):\/\/)?(www.)?(?!.*(ftp|http|https|www.))[a-zA-Z0-9_-]+(\.[a-zA-Z]+)+((\/)[\w#]+)*(\/\w+\?[a-zA-Z0-9_]+=\w+(&[a-zA-Z0-9_]+=\w+)*)?\/?$/;
export const HTTP_HTTPS_REGEX: RegExp = /^(http|https):/i;
export const UTC_DATE_REGEX: RegExp = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/;

export const PATIENT_PORTAL_PREFIX: string = 'patient-portal';
export const PATIENT_PORTAL_ROUTE_PREFIX: string = `/${PATIENT_PORTAL_PREFIX}/`;

export const ENABLE_DAYS = {
  get CURRENT_DATE() {
    return moment().toDate();
  },
  DAYS_365: 365,
  DAYS_7: 7,
  DAYS_15: 15,
  DAYS_45: 45,
  DAYS_60: 60,
  DAYS_30: 30,
  DAYS_90: 90,
  PAST_15_DAYS: -15,
  PAST_180_DAYS: -180,
  PAST_90_DAYS: -90,
  PAST_30_DAYS: -30,
  PAST_45_DAYS: -45,
  YEARS_100: 100,
  YEARS_30: 30,
  YEARS_10: 10,
  YEAR_1: 1
} as const;
